import React from 'react';
import PageLayout from './PageLayout';
import SectionHeading from './SectionHeading';
import ContentCard from './ContentCard';
import MarqueeStrip from './MarqueeStrip';
import useReveal from '../useReveal';
import { pageContent } from './pageContent';

function Home() {
  useReveal();
  const cards = pageContent.home.cards;
  return (
    <PageLayout
      pageKey="home"
      heroVisual={
        <div className="hero-city" aria-hidden="true">
          <div className="hero-city__bar">
            <span className="hc-tag">Databuilder &middot; Applied AI</span>
            <span className="hc-live"><i className="hc-live-dot" />In production</span>
          </div>

          <div className="hero-city__canvas">
            <svg className="hc-scene" viewBox="0 0 400 230">
              {/* AI core + radiating signal */}
              <circle className="hc-core" cx="200" cy="26" r="4.5" />
              <circle className="hc-ring" cx="200" cy="26" r="9" />
              <circle className="hc-ring hc-ring--2" cx="200" cy="26" r="9" />

              {/* Energy beams: core → rooftops */}
              <g className="hc-beams">
                <line x1="197" y1="33" x2="64" y2="136" />
                <line x1="198" y1="34" x2="130" y2="92" />
                <line x1="200" y1="34" x2="197" y2="124" />
                <line x1="202" y1="34" x2="270" y2="138" />
                <line x1="203" y1="33" x2="340" y2="154" />
              </g>

              {/* Signal pulses travelling down the beams */}
              <circle className="hc-pulse" style={{ '--tx': '-136px', '--ty': '106px', fill: '#e11d48', animationDelay: '2.2s' }} cx="200" cy="30" r="2.6" />
              <circle className="hc-pulse" style={{ '--tx': '-70px', '--ty': '61px', fill: '#d97706', animationDelay: '3.6s' }} cx="200" cy="30" r="2.6" />
              <circle className="hc-pulse" style={{ '--tx': '-3px', '--ty': '93px', fill: '#e11d48', animationDelay: '5s' }} cx="200" cy="30" r="2.6" />
              <circle className="hc-pulse" style={{ '--tx': '70px', '--ty': '107px', fill: '#16a34a', animationDelay: '6.4s' }} cx="200" cy="30" r="2.6" />
              <circle className="hc-pulse" style={{ '--tx': '140px', '--ty': '123px', fill: '#5940e1', animationDelay: '7.8s' }} cx="200" cy="30" r="2.6" />

              {/* Ground */}
              <line className="hc-ground" x1="14" y1="204" x2="386" y2="204" />
              <g className="hc-ticks">
                <line x1="40" y1="207" x2="40" y2="211" />
                <line x1="104" y1="207" x2="104" y2="211" />
                <line x1="168" y1="207" x2="168" y2="211" />
                <line x1="232" y1="207" x2="232" y2="211" />
                <line x1="296" y1="207" x2="296" y2="211" />
                <line x1="360" y1="207" x2="360" y2="211" />
              </g>

              {/* House */}
              <g className="hc-b" style={{ '--d': '0.15s' }}>
                <rect className="hc-build" pathLength="1" x="40" y="164" width="48" height="40" />
                <polyline className="hc-build" pathLength="1" points="36 164 64 140 92 164" />
                <rect className="hc-build hc-door" pathLength="1" x="58" y="184" width="12" height="20" />
                <g className="hc-windows">
                  <rect x="46" y="171" width="9" height="9" />
                  <rect x="73" y="171" width="9" height="9" />
                </g>
              </g>

              {/* Tower block */}
              <g className="hc-b" style={{ '--d': '0.35s' }}>
                <rect className="hc-build" pathLength="1" x="108" y="96" width="44" height="108" />
                <rect className="hc-build" pathLength="1" x="125" y="188" width="10" height="16" />
                <g className="hc-windows">
                  <rect x="115" y="104" width="9" height="11" />
                  <rect x="136" y="104" width="9" height="11" />
                  <rect x="115" y="124" width="9" height="11" className="tw" />
                  <rect x="136" y="124" width="9" height="11" />
                  <rect x="115" y="144" width="9" height="11" />
                  <rect x="136" y="144" width="9" height="11" className="tw" />
                  <rect x="115" y="164" width="9" height="11" />
                  <rect x="136" y="164" width="9" height="11" />
                </g>
              </g>

              {/* Hospital */}
              <g className="hc-b" style={{ '--d': '0.55s' }}>
                <rect className="hc-build" pathLength="1" x="168" y="128" width="58" height="76" />
                <rect className="hc-build" pathLength="1" x="190" y="188" width="14" height="16" />
                <path className="hc-build hc-cross" pathLength="1" d="M197 141v16M189 149h16" />
                <g className="hc-windows">
                  <rect x="175" y="168" width="9" height="10" />
                  <rect x="193" y="168" width="9" height="10" className="tw" />
                  <rect x="211" y="168" width="9" height="10" />
                </g>
              </g>

              {/* Civic building */}
              <g className="hc-b" style={{ '--d': '0.75s' }}>
                <polyline className="hc-build" pathLength="1" points="242 160 270 142 298 160" />
                <line className="hc-build" pathLength="1" x1="244" y1="164" x2="296" y2="164" />
                <line className="hc-build" pathLength="1" x1="250" y1="168" x2="250" y2="204" />
                <line className="hc-build" pathLength="1" x1="263" y1="168" x2="263" y2="204" />
                <line className="hc-build" pathLength="1" x1="276" y1="168" x2="276" y2="204" />
                <line className="hc-build" pathLength="1" x1="289" y1="168" x2="289" y2="204" />
              </g>

              {/* Terrace */}
              <g className="hc-b" style={{ '--d': '0.95s' }}>
                <polyline className="hc-build" pathLength="1" points="314 172 327 158 340 172 353 158 366 172" />
                <rect className="hc-build" pathLength="1" x="314" y="172" width="52" height="32" />
                <line className="hc-build" pathLength="1" x1="340" y1="172" x2="340" y2="204" />
                <g className="hc-windows">
                  <rect x="322" y="180" width="9" height="9" className="tw" />
                  <rect x="348" y="180" width="9" height="9" />
                </g>
              </g>

              {/* People walking the street */}
              <g transform="translate(0 204)">
                {/* walks home, disappears through the front door */}
                <g className="hc-walker hc-walker--1">
                  <g className="hc-walker__bob">
                    <circle cx="0" cy="-16.5" r="2.8" />
                    <path d="M0 -13.2V-6" />
                    <path d="M-3.5 -10.5h7" />
                    <path className="hc-legs hc-legs--a" d="M0 -6l-3.4 6M0 -6l3.4 6" />
                    <path className="hc-legs hc-legs--b" d="M0 -6l-1 6M0 -6l1 6" />
                  </g>
                </g>
              </g>
              <g transform="translate(0 204)">
                {/* paces the street */}
                <g className="hc-walker hc-walker--2">
                  <g className="hc-walker__bob">
                    <circle cx="0" cy="-16.5" r="2.8" />
                    <path d="M0 -13.2V-6" />
                    <path d="M-3.5 -10.5h7" />
                    <path className="hc-legs hc-legs--a" d="M0 -6l-3.4 6M0 -6l3.4 6" />
                    <path className="hc-legs hc-legs--b" d="M0 -6l-1 6M0 -6l1 6" />
                  </g>
                </g>
              </g>
              <g transform="translate(12 204)">
                {/* child trailing behind */}
                <g className="hc-walker hc-walker--3">
                  <g className="hc-walker__bob">
                    <circle cx="0" cy="-10.8" r="1.8" />
                    <path d="M0 -8.9V-4" />
                    <path d="M-2.2 -7h4.4" />
                    <path className="hc-legs hc-legs--a" d="M0 -4l-2.1 4M0 -4l2.1 4" />
                    <path className="hc-legs hc-legs--b" d="M0 -4l-0.7 4M0 -4l0.7 4" />
                  </g>
                </g>
              </g>
            </svg>
          </div>

          <div className="hero-city__line">
            <span>Building better with AI &mdash; for</span>
            <span className="hc-carousel">
              <span className="hc-carousel__track">
                <i>communities</i>
                <i>healthcare</i>
                <i>housing</i>
                <i>public services</i>
                <i>communities</i>
              </span>
            </span>
          </div>

          <div className="hero-city__foot">
            <span className="hc-caption">AI products in service of society</span>
            <span className="hc-meter">
              <i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i />
            </span>
          </div>
        </div>
      }
    >

      <main className="App-main" id="services">

        <MarqueeStrip items={pageContent.home.marquee} />

        <section className="section">
          <SectionHeading {...pageContent.home.sections.why} />
          <div className="pitch-grid">
            {cards.why.map((card) => (
              <ContentCard
                key={card.title}
                className="pitch-card"
                icon={card.icon}
                title={card.title}
                description={card.description}
              />
            ))}
          </div>
        </section>

        <section className="section section--alt">
          <SectionHeading {...pageContent.home.sections.innovation} />
          <div className="spectrum-grid">
            {cards.innovation.map((card) => (
              <ContentCard
                key={card.stage}
                className="spectrum-card"
                stage={card.stage}
                title={card.title}
                description={card.description}
                items={card.items}
              />
            ))}
          </div>
        </section>

        <section className="section">
          <SectionHeading {...pageContent.home.sections.services} />
          <div className="service-grid">
            {cards.services.map((card) => (
              <ContentCard
                key={card.title}
                className="service-card"
                title={card.title}
                description={card.description}
                items={card.items}
                ctaLabel={card.ctaLabel}
                ctaHref={card.ctaHref}
                ctaClassName={card.ctaClassName}
              />
            ))}
          </div>
        </section>

        <section className="section section--alt">
          <div className="stats-grid">
            {cards.stats.map((stat) => (
              <div className="stat" key={stat.value}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

      </main>

    </PageLayout>
  );
}

export default Home;
